import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { Loader2, AlertCircle, Link as LinkIcon } from 'lucide-react';
import logo from '@/assets/logo.png';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { z } from 'zod';
import { ChangePasswordForm } from '@/components/ChangePasswordForm';
import { useLoginLockout } from '@/hooks/useLoginLockout';

const usernameSchema = z.string().min(3, 'Username must be at least 3 characters').regex(/^[a-zA-Z0-9_.]+$/, 'Username can only contain letters, numbers, underscores, and periods');
const passwordSchema = z.string().min(6, 'Password must be at least 6 characters');

// Separate component to avoid hooks issues
const PendingApprovalView: React.FC<{
  onCheckApproval: () => Promise<boolean>;
  onSignOut: () => Promise<void>;
  navigate: (path: string) => void;
}> = ({ onCheckApproval, onSignOut, navigate }) => {
  React.useEffect(() => {
    const interval = setInterval(async () => {
      const approved = await onCheckApproval();
      if (approved) {
        navigate('/');
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [onCheckApproval, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-workspace p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="flex items-center justify-center mb-2">
            <img src={logo} alt="LabelBatch logo" className="w-24 h-24 object-contain" />
          </div>
          <CardTitle>Pending Approval</CardTitle>
          <CardDescription>Your account is waiting for admin approval</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              An administrator needs to approve your account before you can access the application.
              This page will automatically redirect once approved.
            </AlertDescription>
          </Alert>
          <div className="text-center text-sm text-muted-foreground">
            <Loader2 className="w-4 h-4 animate-spin inline mr-2" />
            Checking approval status...
          </div>
          <Button 
            variant="outline" 
            className="w-full"
            onClick={onSignOut}
          >
            Sign Out
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

const AuthPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, loading, signInWithUsername, signOut, isApproved, checkApproval, mustChangePassword, changePassword } = useAuth();
  const { isLockedOut, recordFailedAttempt, clearAttempts } = useLoginLockout();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<{ username?: string; password?: string }>({});
  const [pendingApproval, setPendingApproval] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [lockoutMessage, setLockoutMessage] = useState<string | null>(null);
  const inviteCode = searchParams.get('invite');

  useEffect(() => {
    if (!loading && user) {
      if (!isApproved) {
        setPendingApproval(true);
      } else if (mustChangePassword) {
        setShowChangePassword(true);
      } else {
        navigate('/');
      }
    }
  }, [user, loading, isApproved, mustChangePassword, navigate]);

  // Poll for approval status if pending
  useEffect(() => {
    if (pendingApproval && user) {
      const interval = setInterval(async () => {
        const approved = await checkApproval();
        if (approved) {
          setPendingApproval(false);
          navigate('/');
        }
      }, 5000);

      return () => clearInterval(interval);
    }
  }, [pendingApproval, user, checkApproval, navigate]);

  // Process invite code after successful signup/login
  const processInviteCode = async (userId: string) => {
    if (!inviteCode) return;

    try {
      // Validate invite code
      const { data: invite } = await supabase
        .from('invite_links')
        .select('*')
        .eq('code', inviteCode)
        .eq('is_active', true)
        .maybeSingle();

      if (!invite) return;

      // Check if expired
      if (invite.expires_at && new Date(invite.expires_at) < new Date()) return;

      // Check if used up
      if (invite.max_uses > 0 && invite.uses >= invite.max_uses) return;

      // Auto-approve if set
      if (invite.auto_approve) {
        await supabase
          .from('profiles')
          .update({ is_approved: true })
          .eq('user_id', userId);
      }

      // Assign to groups
      const groupIds = (invite.group_ids as string[]) || [];
      if (groupIds.length > 0) {
        const inserts = groupIds.map(gid => ({ user_id: userId, group_id: gid }));
        await supabase.from('user_groups').insert(inserts);
      }

      // Increment uses
      await supabase
        .from('invite_links')
        .update({ uses: invite.uses + 1 })
        .eq('id', invite.id);

      if (invite.auto_approve) {
        toast.success('Account auto-approved via invite link!');
      }
    } catch (e) {
      console.error('Failed to process invite code:', e);
    }
  };

  const validateForm = () => {
    const newErrors: { username?: string; password?: string } = {};
    
    const usernameResult = usernameSchema.safeParse(username);
    if (!usernameResult.success) {
      newErrors.username = usernameResult.error.errors[0].message;
    }

    const passwordResult = passwordSchema.safeParse(password);
    if (!passwordResult.success) {
      newErrors.password = passwordResult.error.errors[0].message;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    // Check lockout before attempting login
    const lockStatus = isLockedOut(username);
    if (lockStatus.locked) {
      const mins = Math.ceil(lockStatus.remainingSeconds / 60);
      setLockoutMessage(`Account locked. Try again in ${mins} minute${mins !== 1 ? 's' : ''}.`);
      toast.error(`Too many failed attempts. Try again in ${mins} minute${mins !== 1 ? 's' : ''}.`);
      return;
    }
    setLockoutMessage(null);

    setIsSubmitting(true);
    const { error, needsApproval, mustChangePassword: needsPasswordChange } = await signInWithUsername(username, password);
    setIsSubmitting(false);

    if (error) {
      if (error.message.includes('Invalid login credentials')) {
        const result = recordFailedAttempt(username);
        if (result.locked) {
          setLockoutMessage(`Account locked for ${result.lockoutMinutes} minute${result.lockoutMinutes !== 1 ? 's' : ''} due to too many failed attempts.`);
          toast.error(`Account locked for ${result.lockoutMinutes} minutes`);
        } else {
          toast.error(`Invalid username or password. ${result.attemptsLeft} attempt${result.attemptsLeft !== 1 ? 's' : ''} remaining.`);
        }
      } else {
        toast.error(error.message);
      }
    } else if (needsApproval) {
      clearAttempts(username);
      // Process invite code which may auto-approve
      if (inviteCode) {
        const { data: { user: currentUser } } = await supabase.auth.getUser();
        if (currentUser) {
          await processInviteCode(currentUser.id);
          const approved = await checkApproval();
          if (approved) {
            toast.success('Welcome! Your account has been approved.');
            navigate('/');
            return;
          }
        }
      }
      setPendingApproval(true);
      toast.info('Your account is pending admin approval');
    } else if (needsPasswordChange) {
      clearAttempts(username);
      setShowChangePassword(true);
      toast.info('Please set a new password');
    } else {
      clearAttempts(username);
      toast.success('Welcome back!');
      navigate('/');
    }
  };

  const handlePasswordChange = async (newPassword: string) => {
    const result = await changePassword(newPassword);
    if (!result.error) {
      toast.success('Password updated successfully!');
      setShowChangePassword(false);
      navigate('/');
    }
    return result;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-workspace">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (pendingApproval) {
    return (
      <PendingApprovalView 
        onCheckApproval={checkApproval}
        onSignOut={async () => {
          await signOut();
          setPendingApproval(false);
          setShowChangePassword(false);
        }}
        navigate={navigate}
      />
    );
  }

  if (showChangePassword) {
    return (
      <ChangePasswordForm
        onChangePassword={handlePasswordChange}
        isFirstLogin={true}
      />
    );
  }

  return (
    <main className="min-h-screen flex items-center justify-center bg-workspace p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="flex items-center justify-center mb-2">
            <img src={logo} alt="LabelBatch logo" className="w-24 h-24 object-contain" fetchPriority="high" />
          </div>
          <CardTitle>Sign In</CardTitle>
          <CardDescription>Enter your credentials to access the application</CardDescription>
          {inviteCode && (
            <div className="flex items-center justify-center gap-2 mt-2 text-sm text-primary">
              <LinkIcon className="w-4 h-4" />
              Invite link detected — sign in to activate
            </div>
          )}
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSignIn} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="username">Username</Label>
              <Input
                id="username"
                type="text"
                placeholder="your_username"
                value={username}
                onChange={(e) => setUsername(e.target.value.toLowerCase())}
                disabled={isSubmitting}
                autoComplete="username"
              />
              {errors.username && (
                <p className="text-sm text-destructive">{errors.username}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={isSubmitting}
                autoComplete="current-password"
              />
              {errors.password && (
                <p className="text-sm text-destructive">{errors.password}</p>
              )}
            </div>
            {lockoutMessage && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{lockoutMessage}</AlertDescription>
              </Alert>
            )}
            <Button type="submit" className="w-full" disabled={isSubmitting || !!lockoutMessage}>
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Signing in...
                </>
              ) : (
                'Sign In'
              )}
            </Button>
          </form>
          <p className="text-xs text-center text-muted-foreground mt-4">
            Contact your administrator if you need an account
          </p>
        </CardContent>
      </Card>
    </main>
  );
};

export default AuthPage;
